<template>
  <div class="booking-pois-container">
    <!-- Compact Loading Overlay -->
    <div v-if="showLoader" class="loading-overlay">
      <div class="loading-spinner-compact">
        <div class="spinner-dot"></div>
        <div class="spinner-dot"></div>
        <div class="spinner-dot"></div>
      </div>
    </div>
    
    <div v-if="showBookingView" class="booking-content">
      <!-- Compact Date Selection Card -->
      <div class="date-selection-section">
        <div class="compact-date-card glass-card" @click="openRangeDatePicker">
          <div class="date-row">
            <div class="date-item">
              <div class="date-info">
                <span class="date-label">{{ $t('booking.start') }}</span>
                <div class="date-value">{{ moment(range.start).format('MMM DD') }}</div>
                <div class="time-value">{{ moment(range.start).format('HH:mm') }}</div>
              </div>
            </div>
            
            <div class="date-arrow">
              <v-icon size="20">mdi-arrow-right</v-icon>
            </div>
            
            <div class="date-item">
              <div class="date-info">
                <span class="date-label">{{ $t('booking.end') }}</span>
                <div class="date-value">{{ moment(range.end).format('MMM DD') }}</div>
                <div class="time-value">{{ moment(range.end).format('HH:mm') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Compact Filters and Results Header -->
      <div class="filters-section">
        <div class="compact-results-bar">
          <div class="results-count">
            <v-icon size="18" class="count-icon">mdi-map-marker-multiple</v-icon>
            <span class="count-text">{{ availablePois?.length || 0 }} {{ $t('booking.available') }}</span>
          </div>
          
          <button class="filter-btn-compact" @click="openDialog">
            <v-icon size="18">mdi-filter-variant</v-icon>
            <span>{{ $t('booking.filter') }}</span>
          </button>
        </div>
      </div>

      <!-- POI Grid -->
      <div class="pois-grid" v-if="availablePois?.length > 0">
        <transition-group 
          name="poi-item" 
          tag="div" 
          class="grid-container"
          @enter="onPoiEnter"
          @leave="onPoiLeave"
        >
          <div 
            v-for="(poi, index) in availablePois" 
            :key="poi.id"
            :data-index="index"
            class="poi-card-wrapper"
            @click="goToPoi(poi)"
          >
            <div class="poi-card glass-card">
              <div class="card-gradient-bg"></div>
              
              <div class="poi-header">
                <h3 class="poi-name">{{ poi?.name }}</h3>
                <div class="poi-category">
                  <v-icon size="16">mdi-tag</v-icon>
                  <span>{{ poi?.category?.name }}</span>
                </div>
              </div>

              <div class="poi-details">
                <div class="detail-item">
                  <v-icon size="18" class="detail-icon">mdi-stairs</v-icon>
                  <span>{{ $t('booking.floor') }} {{ poi?.floor?.number }}</span>
                </div>
                
                <div v-if="poi.capacity" class="detail-item">
                  <v-icon size="18" class="detail-icon">mdi-account-group</v-icon>
                  <span>{{ poi.capacity }} {{ $t('booking.people') }}</span>
                </div>
              </div>

              <div class="poi-actions">
                <button 
                  class="book-btn"
                  :disabled="!IS_PIA"
                  @click.stop="goToPoi(poi)"
                >
                  <v-icon size="18">mdi-calendar-plus</v-icon>
                  <span>{{ $t('booking.book') }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-card glass-card">
          <div class="empty-icon">
            <v-icon size="64">mdi-calendar-remove</v-icon>
          </div>
          <h3 class="empty-title">{{ $t('booking.no-poi') }}</h3>
          <p class="empty-subtitle">{{ $t('booking.custom-date-range') }}</p>
          <button class="glass-btn" @click="openDialog">
            <v-icon size="18">mdi-filter-variant</v-icon>
            <span>{{ $t('booking.filter') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Compact Filter Dialog -->
    <v-dialog v-model="dialog" max-width="400" class="compact-dialog">
      <div class="compact-dialog-content glass-card">
        <div class="compact-dialog-header">
          <h3 class="dialog-title-compact">{{ $t('booking.filter') }}</h3>
          <button class="close-btn-compact" @click="dialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="compact-dialog-body">
          <div class="filter-item">
            <label class="compact-label">{{ $t('office.building') }}</label>
            <v-select 
              :items="buildings" 
              item-value="name" 
              item-title="name" 
              v-model="selectedBuilding"
              class="compact-select"
              disabled 
              return-object
              density="compact"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.floor') }}</label>
            <v-select 
              v-model="selectedFloor" 
              :items="floors" 
              item-title="name" 
              item-value="name" 
              return-object
              class="compact-select"
              density="compact"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.category') }}</label>
            <v-select 
              v-model="selectedCategory" 
              :items="poiCategories" 
              item-title="name" 
              item-value="name" 
              return-object
              class="compact-select"
              density="compact"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.capacity') }}</label>
            <v-select 
              v-model="selectedPersonCount" 
              :items="personCount" 
              item-title="count"
              class="compact-select"
              density="compact"
            />
          </div>
        </div>

        <div class="compact-dialog-actions">
          <button class="cancel-btn-compact" @click="cancelFilter">{{ $t('booking.cancel') }}</button>
          <button class="apply-btn-compact" @click="applyFilters">{{ $t('booking.apply') }}</button>
        </div>
      </div>
    </v-dialog>

    <!-- Compact Date Picker Modal -->
    <div v-if="datePickerDialog" class="compact-date-picker-modal">
      <div class="modal-backdrop" @click="discardRange"></div>
      <div class="compact-date-picker-container">
        <div class="compact-picker-header">
          <h4>{{ $t('booking.date_range') }}</h4>
          <p v-if="isRangeEqual" class="error-text">{{ $t('booking.time-error') }}</p>
        </div>
        
        <div class="compact-picker-content">
          <DatePicker 
            is-range 
            mode="dateTime" 
            v-model.range="tempRange" 
            :rules="timeRules" 
            :min-date="new Date()"
            v-if="showDatePicker" 
            :max-date="moment().add(59, 'days').toDate()" 
            is24hr 
            :is-dark="true"
            @dayclick="(_, event) => { event.target.blur(); }" 
            @update:modelValue="validateTimeRange"
          />
        </div>

        <div class="compact-picker-actions">
          <button class="cancel-btn-compact" @click="discardRange">{{ $t('booking.cancel') }}</button>
          <button 
            class="confirm-btn-compact" 
            @click="confirmRange" 
            :disabled="isRangeEqual"
          >
            {{ $t('booking.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import lodash from 'lodash';

import { DatePicker } from 'v-calendar';
import router from '@/router';
import { showToast } from '@/services/utils';
import {
  getReservationsByPoi,
  getPoiCategoryById,
  getFloorById,
  getFloors,
  getPoiCategories,
  getBuildings,
  getPoisByCategoryId,
  isPIA
} from '@/controllers/BaseController';
import { getStoredItem } from '@/services/utils.js';

export default defineComponent({
  name: 'BookingPoisList',
  components: { DatePicker },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    
    const range = ref({ start: new Date(), end: new Date() });
    const tempRange = ref({ ...range.value });
    const showLoader = ref(true);
    const allPois = ref([]);
    const availablePois = ref([]);
    const poiCategories = ref([]);
    const buildings = ref([]);
    const floors = ref([]);
    const selectedBuilding = ref(null);
    const selectedFloor = ref(null);
    const selectedCategory = ref(null);
    const selectedPersonCount = ref(1);
    const showBookingView = ref(true);
    const datePickerDialog = ref(false);
    const dialog = ref(false);
    const showDatePicker = ref(true);
    const IS_PIA = ref(false);

    const personCount = ref(Array.from({ length: 10 }, (_, i) => ({ count: i + 1 })));
    const originalFilters = ref({ selectedFloor: '', selectedCategory: '', selectedPersonCount: '' });

    const isRangeEqual = computed(() =>
      moment(tempRange.value.start).isSame(tempRange.value.end, 'minute')
    );

    const timeRules = computed(() => [{
      hours: h => h >= 6 && h <= 20,
      minutes: (m, { hours }) => hours === 20 ? m === 0 : m % 15 === 0,
    }]);

    const onPoiEnter = (el) => {
      const index = parseInt(el.dataset.index);
      /* eslint-disable */ 
      gsap.set(el, { opacity: 0, y: 30, scale: 0.9 });
      gsap.to(el, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        delay: index * 0.1,
        ease: "back.out(1.7)"
      });
    };

    const onPoiLeave = (el) => {
      gsap.to(el, {
        opacity: 0,
        scale: 0.8,
        duration: 0.3,
        ease: "power2.in"
      });
    };

    const validateTimeRange = async (r) => {
      const start = moment(r.start);
      const end = moment(r.end);
      const adjusted = { ...r };

      if (start.hour() === 20) {
        showToast({ text: t('booking.start-time-error'), type: 'error', duration: 3000 });
        adjusted.start = start.clone().set({ hour: 19, minute: 45 }).toDate();
        adjusted.end = start.clone().add(15, 'minutes').toDate();
      }

      if (end.isSameOrBefore(start)) {
        adjusted.end = start.clone().add(15, 'minutes').toDate();
      }

      if (end.hour() === 20 && end.minute() > 0) {
        adjusted.end = end.clone().startOf('hour').toDate();
      }

      showDatePicker.value = false;
      tempRange.value = adjusted;
      range.value = adjusted;
      await nextTick();
      showDatePicker.value = true;
    };

    const checkInitialTime = () => {
      const now = moment();
      const minutes = Math.round(now.minute() / 15) * 15;
      let start = moment(now).set({ minute: minutes, second: 0, millisecond: 0 });

      if (now.hour() > 20 || (now.hour() === 20 && now.minute() > 0)) {
        start.add(1, 'day').set({ hour: 6, minute: 0 });
      }

      range.value = {
        start: start.toDate(),
        end: start.clone().set({ hour: 20, minute: 0 }).toDate(),
      };
    };

    const fetchInitialData = async () => {
      try {
        showLoader.value = true;

        const [categories, buildingName, allFloors, allBuildings] = await Promise.all([
          getPoiCategories(),
          getStoredItem('selectedBuilding'),
          getFloors(),
          getBuildings(),
        ]);

        const storedBuilding = lodash.find(allBuildings, { name: buildingName });
        if (!storedBuilding) return;

        selectedBuilding.value = storedBuilding;
        buildings.value = allBuildings;

        const relevantFloors = lodash.chain(allFloors)
          .filter(f => f.building.id === storedBuilding.id && f.active)
          .orderBy('name')
          .value();

        floors.value = [{ id: 1, name: t('booking.all') }, ...relevantFloors];
        selectedFloor.value = floors.value[0];

        const usableCategories = categories.filter(c => !['Showers', 'Restrooms'].includes(c.name));

        const allPoisResult = await Promise.all(
          usableCategories.map(cat => getPoisByCategoryId(cat.id).then(pois => pois.filter(p => p.buildingId === storedBuilding.id)))
        );

        const flat = lodash.flatten(allPoisResult);

        const enrichedCategories = await Promise.all(flat.map(poi => getPoiCategoryById(poi.poiCategories.ids[0])));
        allPois.value = flat;
        poiCategories.value = [{ id: 1, name: t('booking.all') }, ...lodash.uniqBy(lodash.orderBy(enrichedCategories, 'name'), 'id')];
        selectedCategory.value = poiCategories.value.find(c => storedBuilding.name.toLowerCase().includes('hammer') ? c.name?.toLowerCase().includes('meeting') : c.id === 1) || poiCategories.value[0];

        if (IS_PIA.value) {
          selectedCategory.value = poiCategories.value.find(c => c.name?.toLowerCase().includes('meeting')) || poiCategories.value[0];
        }

        checkInitialTime();
        await fetchAvailablePois();
      } catch (e) {
        console.error('Init error:', e);
      } finally {
        showLoader.value = false;
      }
    };

    const fetchAvailablePois = async () => {
      try {
        showLoader.value = true;

        const start = moment(range.value.start).toDate();
        const end = moment(range.value.end).toDate();

        const pois = selectedCategory.value?.id === 1 ? allPois.value : await getPoisByCategoryId(selectedCategory.value.id);

        const available = await Promise.all(
          pois.map(async poi => {
            const reservations = await getReservationsByPoi(poi.id, start, end);
            if (reservations.length === 0) {
              const [category, floor] = await Promise.all([
                getPoiCategoryById(poi.poiCategories.ids[0]),
                getFloorById(poi.floors.ids[0]),
              ]);
              return { ...poi, category, floor };
            }
            return null;
          })
        );

        let filtered = available.filter(Boolean);

        if (selectedFloor.value?.id !== 1) {
          filtered = filtered.filter(poi => poi.floors.ids[0] === selectedFloor.value.id);
        }

        if (selectedPersonCount.value !== 1) {
          filtered = filtered.filter(poi => poi.capacity >= selectedPersonCount.value);
        }

        availablePois.value = filtered;
      } catch (e) {
        console.error('Failed to fetch POIs:', e);
      } finally {
        showLoader.value = false;
      }
    };

    const goToPoi = async poi => {
      let POI = JSON.parse(JSON.stringify(poi))
      try {
        showLoader.value = true
        await router.push({
          name: 'createManageBooking',
          params: {
            building_id: POI.buildingId,
            category_id: POI.category.id,
            poi_id: POI.id,
          },
        })
      } catch (error) {
        console.error('Failed to navigate to POI:', error)
      } finally {
        showLoader.value = false
      }
    };

    const openRangeDatePicker = () => {
      tempRange.value = { ...range.value };
      datePickerDialog.value = true;
    };

    const confirmRange = async () => {
      range.value = { ...tempRange.value };
      datePickerDialog.value = false;
      await fetchAvailablePois();
    };

    const discardRange = () => (datePickerDialog.value = false);

    const openDialog = () => {
      originalFilters.value = {
        selectedFloor: selectedFloor.value,
        selectedCategory: selectedCategory.value,
        selectedPersonCount: selectedPersonCount.value,
      };
      dialog.value = true;
    };

    const cancelFilter = () => {
      dialog.value = false;
      selectedFloor.value = originalFilters.value.selectedFloor;
      selectedCategory.value = originalFilters.value.selectedCategory;
      selectedPersonCount.value = originalFilters.value.selectedPersonCount;
    };

    const applyFilters = async () => {
      dialog.value = false;
      await fetchAvailablePois();
    };

    const handleRouteChange = async () => {
      if (route.name === 'BookingPoisList') {
        showBookingView.value = true;
        await fetchInitialData();
      } else {
        showLoader.value = false;
        showBookingView.value = false;
      }
    };

    onMounted(async () => {
      IS_PIA.value = await isPIA();
      await handleRouteChange();
    });

    watch(route, handleRouteChange);

    return {
      showLoader,
      range,
      tempRange,
      showDatePicker,
      isRangeEqual,
      timeRules,
      buildings,
      floors,
      selectedBuilding,
      selectedFloor,
      selectedCategory,
      selectedPersonCount,
      showBookingView,
      availablePois,
      personCount,
      poiCategories,
      datePickerDialog,
      dialog,
      openDialog,
      cancelFilter,
      applyFilters,
      validateTimeRange,
      openRangeDatePicker,
      confirmRange,
      discardRange,
      goToPoi,
      onPoiEnter,
      onPoiLeave,
      moment,
      IS_PIA,
      t,
    };
  }
});
</script>


<style scoped>
.booking-pois-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  position: relative;
}

/* Compact Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner-compact {
  display: flex;
  gap: 4px;
}

.spinner-dot {
  width: 8px;
  height: 8px;
  background: #4fc3f7;
  border-radius: 50%;
  animation: dot-bounce 1.4s infinite ease-in-out both;
}

.spinner-dot:nth-child(1) { animation-delay: -0.32s; }
.spinner-dot:nth-child(2) { animation-delay: -0.16s; }
.spinner-dot:nth-child(3) { animation-delay: 0s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1.2); opacity: 1; }
}

/* Glass Card Base */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  box-shadow: 
    0 8px 32px rgba(31, 38, 135, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 16px 48px rgba(31, 38, 135, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

/* Compact Date Selection */
.date-selection-section {
  margin-bottom: 20px;
}

.compact-date-card {
  cursor: pointer;
  overflow: hidden;
  position: relative;
  padding: 16px 20px;
}

.compact-date-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(31, 38, 135, 0.15);
}

.date-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.date-item {
  flex: 1;
  text-align: center;
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-label {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-value {
  font-size: 16px;
  font-weight: 600;
  color: #2196f3;
}

.time-value {
  font-size: 14px;
  font-weight: 500;
  color: #4fc3f7;
  background: rgba(79, 195, 247, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
}

.date-arrow {
  color: #4fc3f7;
  opacity: 0.7;
  margin: 0 16px;
}

/* Compact Filters Section */
.filters-section {
  margin-bottom: 20px;
}

.compact-results-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
}

.results-count {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(76, 175, 80, 0.1);
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.count-icon {
  color: #4caf50;
}

.count-text {
  font-weight: 500;
  color: #388e3c;
  font-size: 14px;
}

.filter-btn-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 12px;
  color: #1976d2;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.filter-btn-compact:hover {
  background: rgba(33, 150, 243, 0.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

/* Main Content */
.booking-content {
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
}

/* POI Grid */
.pois-grid {
  min-height: 300px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.poi-card-wrapper {
  cursor: pointer;
}

.poi-card {
  position: relative;
  padding: 20px;
  min-height: 260px;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 24px;
}

.card-gradient-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(79, 195, 247, 0.08) 0%, rgba(33, 150, 243, 0.05) 100%);
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.poi-card:hover .card-gradient-bg {
  opacity: 1;
}

.poi-header {
  margin-bottom: 20px;
  z-index: 1;
}

.poi-name {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 10px 0;
  line-height: 1.3;
  letter-spacing: -0.2px;
}

.poi-category {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #2196f3;
  font-size: 13px;
  font-weight: 500;
  background: rgba(33, 150, 243, 0.1);
  padding: 5px 12px;
  border-radius: 20px;
  width: fit-content;
  transition: background 0.3s ease;
}

.poi-card:hover .poi-category {
  background: rgba(33, 150, 243, 0.15);
}

.poi-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  min-height: 80px;
  z-index: 1;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  transition: color 0.3s ease;
}

.poi-card:hover .detail-item {
  color: #1976d2;
}

.detail-icon {
  color: #2196f3;
  min-width: 24px;
  transition: transform 0.3s ease;
}

.poi-card:hover .detail-icon {
  transform: scale(1.1);
}

.poi-actions {
  margin-top: auto;
  padding-top: 12px;
  z-index: 1;
}

.book-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 18px;
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.book-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(33, 150, 243, 0.3);
  background: linear-gradient(135deg, #4fc3f7 20%, #2196f3 100%);
}

.book-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.empty-card {
  text-align: center;
  padding: 48px 32px;
  max-width: 400px;
}

.empty-icon {
  color: #bbb;
  margin-bottom: 24px;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.empty-subtitle {
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

/* Compact Dialog Styles */
.compact-dialog .v-overlay__content {
  margin: 0;
}

.compact-dialog-content {
  width: 100%;
  max-width: 400px;
  margin: 16px;
  max-height: calc(100vh - 32px);
  overflow: auto;
}

.compact-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(79, 195, 247, 0.2);
}

.dialog-title-compact {
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
}

.close-btn-compact {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-btn-compact:hover {
  background: rgba(0, 0, 0, 0.1);
}

.compact-dialog-body {
  padding: 16px 20px;
}

.filter-item {
  margin-bottom: 16px;
}

.compact-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 13px;
}

.compact-select {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
}

.compact-dialog-actions {
  display: flex;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(79, 195, 247, 0.2);
}

.cancel-btn-compact,
.apply-btn-compact,
.confirm-btn-compact {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cancel-btn-compact {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(33, 150, 243, 0.3);
  color: #1976d2;
}

.cancel-btn-compact:hover {
  background: rgba(33, 150, 243, 0.1);
}

.apply-btn-compact,
.confirm-btn-compact {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  border: none;
}

.apply-btn-compact:hover:not(:disabled),
.confirm-btn-compact:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3);
}

.apply-btn-compact:disabled,
.confirm-btn-compact:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Compact Date Picker Modal */
.compact-date-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.compact-date-picker-container {
  position: relative;
  z-index: 1;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  margin: 16px;
  background: #0F162A;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: none;
}

.compact-picker-header {
  padding: 16px 20px 12px;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.compact-picker-header h4 {
  margin: 0 0 6px 0;
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
}

.error-text {
  color: #f44336;
  font-size: 12px;
  margin: 4px 0 0 0;
}

.compact-picker-content {
  padding: 16px;
  display: flex;
  justify-content: center;
}

.compact-picker-actions {
  display: flex;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Date Picker Modal */
.date-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
}

.date-picker-container {
  position: relative;
  z-index: 1;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  margin: 20px;
}

.date-picker-header {
  padding: 24px 24px 20px;
  text-align: center;
  border-bottom: 1px solid rgba(79, 195, 247, 0.2);
}

.date-picker-header h3 {
  margin: 0 0 8px 0;
  color: #1976d2;
  font-size: 18px;
  font-weight: 600;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin: 8px 0 0 0;
}

.date-picker-content {
  padding: 24px;
  display: flex;
  justify-content: center;
}

.date-picker-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px 24px;
  border-top: 1px solid rgba(79, 195, 247, 0.2);
}

.confirm-btn {
  flex: 1;
}

/* POI Item Animations */
.poi-item-enter-active,
.poi-item-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.poi-item-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(30px);
}

.poi-item-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(-30px);
}

/* Responsive Design - Compact */
@media (max-width: 1200px) {
  .booking-content {
    padding: 14px;
  }
  
  .grid-container {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 968px) {
  .booking-content {
    padding: 12px;
  }
  
  .compact-results-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .grid-container {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
  }
}

@media (max-width: 768px) {
  .booking-content {
    padding: 10px;
  }
  
  .compact-date-card {
    padding: 10px 14px;
  }
  
  .date-value {
    font-size: 15px;
  }
  
  .time-value {
    font-size: 13px;
    padding: 2px 6px;
  }
  
  .date-arrow {
    margin: 0 8px;
  }
  
  .poi-card {
    min-height: 240px;
    padding: 14px;
  }
  
  .grid-container {
    grid-template-columns: 1fr;
  }
  
  .compact-dialog-content {
    margin: 8px;
  }
}

@media (max-width: 450px) {
  .booking-content {
    padding: 8px;
  }
  
  .compact-date-card {
    padding: 8px 12px;
  }
  
  .date-value {
    font-size: 14px;
  }
  
  .time-value {
    font-size: 12px;
    padding: 1px 5px;
  }
  
  .date-arrow {
    margin: 0 6px;
  }
  
  .poi-name {
    font-size: 16px;
  }
  
  .poi-card {
    min-height: 220px;
    padding: 12px;
  }
  
  .compact-date-picker-container {
    margin: 8px;
  }
}

@media (max-width: 355px) {
  .booking-content {
    padding: 6px;
  }
  
  .compact-date-card {
    padding: 6px 10px;
  }
  
  .date-item {
    flex: 1;
    min-width: 0;
  }
  
  .date-value {
    font-size: 13px;
  }
  
  .time-value {
    font-size: 11px;
    padding: 1px 4px;
  }
  
  .date-arrow {
    margin: 0 4px;
  }
  
  .poi-card {
    min-height: 200px;
    padding: 10px;
  }
  
  .poi-name {
    font-size: 15px;
  }
  
  .book-btn {
    padding: 10px 14px;
    font-size: 13px;
  }
  
  .compact-dialog-content {
    margin: 6px;
  }
  
  .compact-dialog-header {
    padding: 12px 16px 10px;
  }
  
  .compact-dialog-body {
    padding: 12px 16px;
  }
  
  .compact-dialog-actions {
    padding: 10px 16px 12px;
  }
  
  .dialog-title-compact {
    font-size: 14px;
  }
}
</style>
<!--
This component, BookingPoisList, provides users with a comprehensive interface for booking Points of Interest (POIs) within a building. 

The main functionalities include:
1. Date and Time Selection: Users can select a start and end date/time range for their booking using a date picker. The selected range is displayed on a card, and clicking this card opens a date picker dialog for adjustments.
2. Available POIs Display: The component displays a list of available POIs based on the selected date/time range and applied filters. Each POI is shown with its name, category, floor number, floor name, and capacity.
3. Filtering Options: Users can filter the available POIs by building, floor, category, and person count using a filter dialog. This dialog is opened by clicking the "Filter" button.
4. Booking POIs: Users can click on a POI card to navigate to a detailed booking page for that specific POI.

Visual Feedback:
- A loader is displayed while data is being fetched or processed.
- The date picker dialog provides error messages if the selected start and end times are the same.
- The filter dialog allows users to apply or cancel filters, with the "Apply" and "Cancel" buttons providing immediate feedback on their actions.

Dynamic Behaviors:
- The list of available POIs updates dynamically based on the selected date/time range and applied filters.
- The date picker dialog ensures that users cannot select invalid times or dates (e.g., weekends or times outside the POI's available hours).
- Clicking on a POI card or the "Book" button navigates the user to a detailed booking page for that POI.

Overall, this component enhances the user experience by providing an intuitive and interactive interface for booking POIs, with clear visual feedback and dynamic updates based on user inputs.
-->